import Swiper, {
    Autoplay,
    Mousewheel,
    Navigation,
    Pagination,
    Scrollbar,
    Thumbs,
    Controller,
    Grid
  } from 'swiper';
  import 'swiper/swiper-bundle.css';
  import gsap from 'gsap';
  import LazyLoader from './LazyLoader';

  Swiper.use([
    Navigation,
    Pagination,
    Autoplay,
    Scrollbar,
    Mousewheel,
    Thumbs,
    Controller,
    Grid
  ]);

class FeaturedContent {
  constructor() {
    this.container = document.querySelector('.featured-content');
    this.featuredSection = document.querySelector('.game-theory-featured');
    this.videoContainer = document.querySelector('.video-embed');
    this.episodeElements = document.querySelectorAll('.featured-episode');
    this.titleElement = document.querySelector('.featured-title');
    this.guestElement = document.querySelector('.featured-guest');
    this.playButton = document.querySelector('.featured-content .thumbnail-buttons a');
    this.thumbnailButtons = document.querySelectorAll('.thumbnail-buttons');
    this.lastUpdateTime = 0;
    this.updateDebounceTime = 300; // ms

    // Create timeline for animations
    this.timeline = gsap.timeline();
  }

  update(index) {
    const now = Date.now();
    // Prevent updates that are too close together
    if (now - this.lastUpdateTime < this.updateDebounceTime) {
      return;
    }
    this.lastUpdateTime = now;

    if (!window.videosData[index]) return;

    const data = window.videosData[index];

    // Kill any existing animations
    this.timeline.kill();
    this.timeline = gsap.timeline();

    // Animate out current content
    this.timeline
      .to([this.videoContainer, ...this.episodeElements, this.titleElement, this.guestElement, ...this.thumbnailButtons], {
        opacity: 0,
        duration: 0.3,
        stagger: 0.05,
        ease: "power2.out"
      })
      .call(() => {
        // Update content while elements are invisible
        this.updateVideo(data);
        this.updateEpisode(data);
        this.updateTitle(data);
        this.updateGuest(data);
        this.updatePlayButton(data, index);
      })
      // Animate in new content
      .to(this.videoContainer, {
        opacity: 1,
        duration: 0.4,
        ease: "power2.out"
      })
      .fromTo([...this.episodeElements, this.titleElement, this.guestElement, ...this.thumbnailButtons],
        {
          opacity: 0,
          x: 20
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.4,
          stagger: 0.1,
          ease: "power2.out"
        },
        "-=0.2" // Start slightly before video animation ends
      );
  }

  updateVideo(data) {
    if (!this.videoContainer) return;

    this.videoContainer.innerHTML = '';
    if (data.video_file) {
      const videoElement = `
        <video class="game-theory-video" width="100%" height="100%" muted autoplay playsinline>
          <source src="${data.video_file.url}" type="${data.video_file.mime_type}">
        </video>
      `;
      this.videoContainer.innerHTML = videoElement;
    }

    const video = this.videoContainer.querySelector('video');
	if (!video) return;

	const controls = this.container.querySelector('.video-controls');
	if (!controls) return;
    const playPauseBtn = controls.querySelector('.play-pause-btn');
    const svg = playPauseBtn.querySelector('svg');
    const playIcon = svg.querySelector('#gt-btn-play');
    const pauseIcon = svg.querySelector('#gt-btn-pause');
    const progressRing = controls.querySelector('.progress-ring');
    const radius = progressRing.r.baseVal.value;
    const circumference = 2 * Math.PI * radius;
    progressRing.style.strokeDasharray = `${circumference}`;
    progressRing.style.strokeDashoffset = `${circumference}`;

    function updateProgress() {
      if (!video.duration) return;
      const percent = video.currentTime / video.duration;
      progressRing.style.strokeDashoffset = `${circumference * (1 - percent)}`;
    }

    // Helper to update icons using GSAP
    function setPlayState(isPlaying) {
      if (isPlaying) {
        gsap.to(playIcon, { opacity: 0, duration: 0.2, overwrite: true });
        gsap.to(pauseIcon, { opacity: 1, duration: 0.2, overwrite: true });
      } else {
        gsap.to(playIcon, { opacity: 1, duration: 0.2, overwrite: true });
        gsap.to(pauseIcon, { opacity: 0, duration: 0.2, overwrite: true });
      }
    }

    // Set initial state
    setPlayState(!video.paused);

    // Play/pause button click
    playPauseBtn.onclick = () => {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    };

    // Update icons on play/pause
    video.addEventListener('play', () => setPlayState(true));
    video.addEventListener('pause', () => setPlayState(false));

    // Progress ring update
    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('loadedmetadata', updateProgress);

    // Auto-advance on end
    video.addEventListener('ended', () => {
      // Try to advance the Swiper slider to next non-disabled slide
      if (window.gtEpisodesSlider && typeof window.gtEpisodesSlider.slideNext === 'function') {
        const currentIndex = window.gtEpisodesSlider.activeIndex;

        if (!window.slidersInstance) {
          return;
        }

        const nextEnabledIndex = window.slidersInstance.findNextEnabledSlide(currentIndex);

        if (nextEnabledIndex !== currentIndex) {
          // Slide to the next enabled slide
          window.gtEpisodesSlider.slideTo(nextEnabledIndex);

          // Wait for Swiper to update, then update featured content
          setTimeout(() => {
            if (window.slidersInstance.featuredContent && window.videosData[nextEnabledIndex]) {
              window.slidersInstance.featuredContent.update(nextEnabledIndex);
            }
          }, 100);
        }
      }
    });
  }

  updateEpisode(data) {
    if (this.episodeElements.length && data.episode) {
      this.episodeElements.forEach(element => {
        element.textContent = `Episode ${data.episode}`;
      });
    }
  }

  updateTitle(data) {
    if (this.titleElement) {
      this.titleElement.textContent = data.title;
      if (data.move_down) {
        this.titleElement.style.marginTop = data.move_down + 'px';
      }
    }
  }

  updateGuest(data) {
    if (this.guestElement) {
      this.guestElement.textContent = data.guest;
    }
  }

  updatePlayButton(data, index) {
    if (this.playButton && data.modal_id) {
      this.playButton.setAttribute('video-id', data.modal_id);
      this.playButton.setAttribute('data-index', index);
    }
  }
}

export default class Sliders {
  constructor() {
    // Initialize videosData if it doesn't exist (for non-game-theory pages)
    if (typeof videosData === 'undefined') {
      window.videosData = [];
    }
    this.init();
  }

  init() {
    this.initSliders();
  }

  initSliders() {
   const featuredContainer = document.querySelector('.featured-content');
   const episodesSlider = document.querySelector('.gt-episodes-slider');
   this.featuredContent = null;

   // Make Sliders instance accessible early for helper methods
   window.slidersInstance = this;

   // Only initialize features if elements exist
   if (featuredContainer) {
		this.featuredContent = new FeaturedContent();
   }

   if (episodesSlider) {
		this.initEpisodesSlider();
   }

   // Always initialize modal thumbnails slider if it exists
   const modalThumbnailsSlider = document.querySelector('.modal-thumbnails-slider');
   if (modalThumbnailsSlider) {
    this.initModalThumbnailsSlider();
  }

   // Update featured content with initial slide data only if both exist
   // Check if videosData has content (could be array or object)
   const hasVideosData = window.videosData && (
     (Array.isArray(window.videosData) && window.videosData.length > 0) ||
     (typeof window.videosData === 'object' && Object.keys(window.videosData).length > 0)
   );

   if (this.featuredContent !== null && hasVideosData) {
		this.featuredContent.update(0);
   }
  }

  initEpisodesSlider() {
    const gtEpisodesSlider = new Swiper(".gt-episodes-slider", {
      slidesPerView: "auto",
      spaceBetween: 20,
      slideToClickedSlide: false,
      roundLengths: true,
      initialSlide: 0,
      navigation: {
        nextEl: ".episodes-button-next",
        prevEl: ".episodes-button-prev",
      },
      slidesPerGroup: 1,
      centeredSlides: false,
      scrollbar: {
        el: ".swiper-scrollbar",
        draggable: true,
      },
      /*breakpoints: {
        768: {
          slidesPerView: 2.5,
        },
        1024: {
          slidesPerView: 3.5,
        },
      },*/
      on: {
        init: () => {
          const lazyLoader = LazyLoader.getInstance();
          if (lazyLoader) {
            setTimeout(() => {
              lazyLoader.loadAllVisibleSlides();
            }, 50);
          }
        },
        slideChange: () => {
          const lazyLoader = LazyLoader.getInstance();
          if (lazyLoader) {
            lazyLoader.loadAllVisibleSlides();
          }
        },
        transitionStart: () => {
          const lazyLoader = LazyLoader.getInstance();
          if (lazyLoader) {
            lazyLoader.loadAllVisibleSlides();
          }
        },
        transitionEnd: () => {
          const lazyLoader = LazyLoader.getInstance();
          if (lazyLoader) {
            lazyLoader.loadAllVisibleSlides();
          }
        }
      }
    });

    // Handle slide clicks
    document.querySelectorAll('.gt-episodes-slider .swiper-slide').forEach((slide, index) => {
      slide.addEventListener('click', (e) => {
        if (!e.target.closest('.play-btn[data-modal]')) {
          // Only navigate to the slide if it's not disabled
          if (!this.isSlideDisabled(index)) {
            gtEpisodesSlider.slideTo(index);
            // Only update featured content if slide is not disabled
            if (this.featuredContent && !this.isSlideDisabled(index)) {
              this.featuredContent.update(index);
            }
          }
        }
      });
    });

    // Handle play button clicks
    document.querySelectorAll('.gt-episodes-slider .play-btn[data-modal]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const modalId = btn.getAttribute('video-id');
        const index = btn.getAttribute('data-index');
        return false;
      });
    });

    // Make Swiper instance accessible
    window.gtEpisodesSlider = gtEpisodesSlider;
  }

  initModalThumbnailsSlider() {
    new Swiper(".modal-thumbnails-slider", {
      slidesPerView: 1.5,
      spaceBetween: 10,
      slideToClickedSlide: true,
      initialSlide: 0,
      navigation: {
        nextEl: ".modal-thumbnails-slider .swiper-button-next",
        prevEl: ".modal-thumbnails-slider .swiper-button-prev",
      },
      slidesPerGroup: 1,
      centeredSlides: true,
      roundLengths: true,
      breakpoints: {
        768: {
          slidesPerView: 3,
        },
        1024: {
          slidesPerView: 4,
        },
        1300: {
          slidesPerView: 5,
        },
      },
      on: {
        init: () => {
          // Load images in initially visible modal thumbnail slides
          const lazyLoader = LazyLoader.getInstance();
          if (lazyLoader) {
            setTimeout(() => {
              lazyLoader.loadModalThumbnailImages();
            }, 100);
          }
        },
        slideChange: () => {
          // Load images in newly visible modal thumbnail slides
          const lazyLoader = LazyLoader.getInstance();
          if (lazyLoader) {
            setTimeout(() => {
              lazyLoader.loadModalThumbnailImages();
            }, 100);
          }
        }
      }
    });
  }

  // Helper method to check if a slide is disabled
  isSlideDisabled(index) {
    const slides = document.querySelectorAll('.gt-episodes-slider .swiper-slide');
    return slides[index] && slides[index].hasAttribute('data-disabled');
  }

  // Helper method to find next non-disabled slide
  findNextEnabledSlide(currentIndex) {
    const slides = document.querySelectorAll('.gt-episodes-slider .swiper-slide');
    const totalSlides = slides.length;

    // Start from next slide
    for (let i = currentIndex + 1; i < totalSlides; i++) {
      if (!this.isSlideDisabled(i)) {
        return i;
      }
    }

    // If no enabled slide found after current, wrap around to beginning
    for (let i = 0; i < currentIndex; i++) {
      if (!this.isSlideDisabled(i)) {
        return i;
      }
    }

    // If all slides are disabled, return current index
    return currentIndex;
  }

  // Cleanup method
  destroy() {
    // Cleanup is now handled in GameTheory.js
  }
}
